import { scrollParent } from '../ui/scrollParent';
import { CAPTION_H, type Placement } from './masonry';

/**
 * The reflow that makes room for the panel moves tiles between columns, so
 * the thing you just clicked frequently ends up somewhere else — often off
 * screen. This puts it back in view.
 *
 * It works from the placement rather than the element's own box because the
 * tiles are mid-transition when this runs: `scrollIntoView` on a tile that is
 * still gliding to its new column aims at where it was, not where it's going.
 */

/** Never flush against the edge of the scroll box. */
const MARGIN = 24;

export function revealTile(grid: HTMLElement, placement: Placement): void {
  const scroller = scrollParent(grid);
  if (scroller === null) return;

  const gridTop =
    grid.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;

  const top = gridTop + placement.y;
  const bottom = top + placement.h + CAPTION_H;
  const viewTop = scroller.scrollTop;
  const viewBottom = viewTop + scroller.clientHeight;

  let next: number | undefined;
  if (top < viewTop + MARGIN) {
    next = top - MARGIN;
  } else if (bottom > viewBottom - MARGIN) {
    // A tile taller than the viewport gets its top shown, not its bottom.
    next = Math.min(bottom + MARGIN - scroller.clientHeight, top - MARGIN);
  }

  if (next === undefined) return;
  scroller.scrollTo({ top: Math.max(0, next), behavior: 'smooth' });
}
